import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plane, Send, Loader2, CheckCircle2, AlertCircle, RotateCcw, Bot, User as UserIcon, Mic, MicOff } from 'lucide-react';
import BackButton from '../components/BackButton';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { getCandidateAnswers, saveCandidateAnswers, getCvQuestions, normalizeCvQuestion,
  isAbroadQuestionVisible, analyzeCandidateAnswer } from '../services/api';
import { ABROAD_QUESTIONS as LOCAL_QUESTIONS } from '../data/cvFlowQuestions';
import './AbroadPage.css';

const INTRO =
  'Merhaba! ABD veya Kanada\'da çalışma planını birlikte netleştirelim. ' +
  'Önce CV bilgilerini, ardından yurt dışı planını adım adım tamamlayacağız. ' +
  'İstediğin an ara verip sonra devam edebilirsin.';

// BE'ye ulaşılamazsa CV asistanındaki sorular kullanılır
const LOCAL_SECTIONS = {
  abroad_profile: 'Kariyer Profili',
  abroad_target: 'Hedef İş ve Ülke',
  abroad_visa: 'Göç ve Vize',
  abroad_value: 'Değer Öneriniz',
  abroad_adapt: 'Esneklik ve Taahhüt',
  abroad_finish: 'Kapanış',
};

const FALLBACK = LOCAL_QUESTIONS.map((q) => ({
  key: q.field,
  section: LOCAL_SECTIONS[q.section] || null,
  text: q.text,
  type: q.type,
  choices: q.choices || [],
}));

// Kaydedilen değeri ekranda gösterilecek etikete çevirir
const displayValue = (q, value) => {
  if (value === null || value === undefined || value === '') return '';
  if (q?.choices?.length) {
    const match = q.choices.find((c) => c.value === value);
    if (match) return match.label;
  }
  return String(value);
};

// Bitiş/ayrılış tarihi soruları API'de month tipinde gelse bile halen çalışan
// kullanıcı "Devam" yazabilmeli. Başlangıç tarihi sorularında month korunur.
const acceptsOngoingAnswer = (question) => {
  const searchable = [question?.key, question?.fieldKey, question?.text]
    .filter(Boolean).join(' ').toLocaleLowerCase('tr-TR');
  return /end.?date|bitiş|ayrıl|ne zaman.*çıkt|halen|devam|present/.test(searchable);
};

const AbroadPage = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [phaseLoading, setPhaseLoading] = useState(false);
  const [phase, setPhase] = useState('cv');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState([]);   // checkbox tipi için
  const [followUp, setFollowUp] = useState(null); // { parentKey, question }
  const [analyzing, setAnalyzing] = useState(false);
  const askedFollowUpRef = useRef({});            // her soru için en fazla 1 ek soru
  const [messages, setMessages] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [finished, setFinished] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [isListening, setIsListening] = useState(false);

  const endRef = useRef(null);
  const answersRef = useRef({});
  const recognitionRef = useRef(null);

  const speechSupported = typeof window !== 'undefined'
    && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setIsListening(false);
  }, []);

  const toggleListening = () => {
    if (isListening) {
      stopListening();
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'tr-TR';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
      }
      setInput(transcript.trimStart());
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setIsListening(false);
    };
    recognition.onerror = (event) => {
      recognitionRef.current = null;
      setIsListening(false);
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setSaveError('Mikrofon erişimi reddedildi. Tarayıcı ayarlarından mikrofon izni verin.');
      }
    };
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  useEffect(() => () => recognitionRef.current?.stop(), []);

  const scrollToEnd = () => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  useEffect(scrollToEnd, [messages, typing]);

  // Soruları ve kayıtlı cevapları birlikte yükle
  useEffect(() => {
    let cancelled = false;

    if (!user?.candidateId) {
      setLoading(false);
      return () => { cancelled = true; };
    }

    // İsteği microtask'e ertele: Strict Mode'un ilk setup/cleanup turu ağa
    // gereksiz bir istek göndermeden iptal edilebilsin.
    const loadQuestions = Promise.resolve()
      .then(() => (cancelled ? [] : getCvQuestions('cv')))
      .then((list) => {
        // İlk fazda API'nin döndürdüğü temel CV sorularının tamamı sorulur.
        const mapped = (list || [])
          .map(normalizeCvQuestion)
          .filter((q) => q.key);
        mapped.sort((a, b) => a.sortOrder - b.sortOrder);
        return mapped;
      })
      .catch(() => []);

    const loadAnswers = Promise.resolve()
      .then(() => (cancelled ? { data: [] } : getCandidateAnswers(user.candidateId)))
      .then((res) => {
        const map = {};
        (res?.data || []).forEach((x) => {
          if (x && String(x.scope || '').toLowerCase() === 'abroad' && x.key && x.value) {
            map[x.key] = x.value;
          }
        });
        return map;
      })
      .catch(() => ({}));

    Promise.all([loadQuestions, loadAnswers])
      .then(([qs, map]) => {
        // React Strict Mode development ortamında effect'i iki kez çalıştırır.
        // Temizlenmiş ilk effect'in ikinci bir soru/faz akışı başlatmasını önle.
        if (cancelled) return;
        setQuestions(qs);
        setAnswers(map);
        answersRef.current = map;

        const firstUnanswered = qs.findIndex((q) => !map[q.key] && isAbroadQuestionVisible(q, map));
        const history = [];
        let lastSection = null;
        qs.forEach((q, i) => {
          if (map[q.key] && (firstUnanswered === -1 || i < firstUnanswered)) {
            if (q.section && q.section !== lastSection) {
              history.push({ role: 'section', text: q.section });
              lastSection = q.section;
            }
            history.push({ role: 'ai', text: q.text });
            history.push({ role: 'user', text: displayValue(q, map[q.key]) });
          }
        });

        if (history.length === 0) history.push({ role: 'ai', text: INTRO });

        if (firstUnanswered === -1) {
          // CV sorusu yoksa veya hepsi daha önce cevaplandıysa ikinci fazı başlat.
          setMessages(history);
          setQuestions(qs);
          startAbroadPhase(map, history);
        } else {
          const q = qs[firstUnanswered];
          const opening = [];
          if (q.section && q.section !== lastSection) opening.push({ role: 'section', text: q.section });
          opening.push({ role: 'ai', text: q.text });
          setCurrentIdx(firstUnanswered);
          setMessages([...history, ...opening]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
    // startAbroadPhase bu effect tamamlandıktan sonra çalıştırılan kararlı bir callback'tir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.candidateId]);

  // CV soruları tamamlandıktan sonra çağrılır; iki GetCvQuestions isteğinin
  // aynı anda atılmaması ürün akışının bir parçasıdır.
  const startAbroadPhase = useCallback(async (currentAnswers, currentMessages) => {
    setTyping(true);
    setPhaseLoading(true);
    setSaveError(null);

    let mapped;
    try {
      const list = await getCvQuestions('abroad');
      mapped = (list || [])
        .map(normalizeCvQuestion)
        // Yeni endpoint scope'a göre ayrılmış liste döndürüyor. fieldKey dolu
        // sorular da Abroad akışının parçası olabileceği için ayıklanmaz.
        .filter((q) => q.key)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      if (!mapped.length) mapped = FALLBACK;
    } catch {
      mapped = FALLBACK;
    }

    setPhase('abroad');
    setQuestions(mapped);
    const firstIdx = mapped.findIndex((q) =>
      !currentAnswers[q.key] && isAbroadQuestionVisible(q, currentAnswers)
    );

    if (firstIdx === -1) {
      setTyping(false);
      setPhaseLoading(false);
      setFinished(true);
      setMessages((previous) => [
        ...(currentMessages || previous),
        { role: 'ai', text: 'Tüm sorular yanıtlandı. İstersen baştan güncelleyebilirsin.' },
      ]);
      return;
    }

    const first = mapped[firstIdx];
    setCurrentIdx(firstIdx);
    setTyping(false);
    setPhaseLoading(false);
    setMessages((previous) => [
      ...(currentMessages || previous),
      { role: 'section', text: 'Yurt Dışı Planı' },
      ...(first.section ? [{ role: 'section', text: first.section }] : []),
      { role: 'ai', text: first.text },
    ]);
  }, []);

  // Her cevap anında kaydedilir, SaveCandidateAnswers upsert olduğu için
  // yalnızca değişen anahtar gönderilir
  // Koşulu sağlanan bir sonraki soruyu bul
  const nextVisibleIndex = useCallback((fromIdx, currentAnswers) => {
    for (let i = fromIdx; i < questions.length; i += 1) {
      if (isAbroadQuestionVisible(questions[i], currentAnswers)) return i;
    }
    return -1;
  }, [questions]);

  const saveOne = useCallback(async (key, value) => {
    if (!user?.candidateId) {
      setSaveError('Önce profilini tamamlamalısın.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await saveCandidateAnswers([{ key, value: value || null }]);
      setSaved(true);
    } catch (e) {
      setSaveError(e.message || 'Cevap kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  }, [user?.candidateId]);

  // Ek soru cevabı ana cevabın sonuna eklenir; key gönderilmez
  const saveFollowUp = useCallback(async (parentKey, question, value) => {
    setSaving(true);
    setSaveError(null);
    try {
      await saveCandidateAnswers([{ parentKey, followUpQuestion: question, value }]);
      setSaved(true);
    } catch (e) {
      setSaveError(e.message || 'Cevap kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  }, []);

  // Sonraki soruya geç (koşulu sağlanmayanları atlar)
  const goToNext = (fromIdx, currentAnswers, prevSection) => {
    const nextIdx = nextVisibleIndex(fromIdx + 1, currentAnswers);
    if (nextIdx === -1) {
      if (phase === 'cv') {
        // Fonksiyonel state güncellemesi son kullanıcı cevabını da korur.
        startAbroadPhase(currentAnswers);
        return;
      }
      setTyping(false);
      setFinished(true);
      setMessages((prev) => [...prev, {
        role: 'ai',
        text: 'Teşekkürler! Yanıtların kaydedildi. Bu bilgiler ABD ve Kanada fırsatlarında sana özel rehberlik için kullanılacak.',
      }]);
      return;
    }

    const nextQ = questions[nextIdx];
    const sectionChanged = nextQ.section && nextQ.section !== prevSection;
    setCurrentIdx(nextIdx);
    setTyping(false);
    setMessages((prev) => [
      ...prev,
      ...(sectionChanged ? [{ role: 'section', text: nextQ.section }] : []),
      { role: 'ai', text: nextQ.text },
    ]);
  };

  const answerCurrent = (value) => {
    const q = questions[currentIdx];
    if (!q || typing || finished || analyzing) return;
    const stored = String(value).trim();
    if (!stored) return;
    stopListening();

    // Ek soru cevaplanıyorsa: kaydet ve normal akışa dön
    if (followUp) {
      setMessages((prev) => [...prev, { role: 'user', text: stored }]);
      setInput('');
      setTyping(true);
      saveFollowUp(followUp.parentKey, followUp.question, stored);
      const parentSection = questions.find((x) => x.key === followUp.parentKey)?.section;
      setFollowUp(null);
      setTimeout(() => goToNext(currentIdx, answersRef.current, parentSection), 550);
      return;
    }

    const next = { ...answersRef.current, [q.key]: stored };

    // Bu cevap yüzünden görünmez hale gelen soruların cevabını temizle
    questions.forEach((other) => {
      if (other.dependsOnKey === q.key && !isAbroadQuestionVisible(other, next)) {
        delete next[other.key];
      }
    });

    answersRef.current = next;
    setAnswers(next);
    setMessages((prev) => [...prev, { role: 'user', text: displayValue(q, stored) }]);
    setInput('');
    setChecked([]);
    setTyping(true);
    saveOne(q.key, stored);

    // Uzun cevaplarda AI ek soru sorabilir; seçim tipinde çağrılmaz
    const canAnalyze = q.aiFollowUp
      && q.type !== 'choice'
      && q.type !== 'checkbox'
      && !askedFollowUpRef.current[q.key];

    if (!canAnalyze) {
      setTimeout(() => goToNext(currentIdx, next, q.section), 550);
      return;
    }

    askedFollowUpRef.current[q.key] = true;
    setAnalyzing(true);
    analyzeCandidateAnswer({ key: q.key, value: stored })
      .then((res) => {
        if (res?.isSufficient === false && res.followUpQuestion) {
          setFollowUp({ parentKey: res.parentKey || q.key, question: res.followUpQuestion });
          setTyping(false);
          setMessages((prev) => [...prev, { role: 'ai', text: res.followUpQuestion }]);
          return;
        }
        goToNext(currentIdx, next, q.section);
      })
      .catch(() => goToNext(currentIdx, next, q.section))
      .finally(() => setAnalyzing(false));
  };

  const restart = () => {
    answersRef.current = {};
    setAnswers({});
    setCurrentIdx(0);
    setPhase('cv');
    setFinished(false);
    setSaved(false);
    setSaveError(null);
    setChecked([]);
    setPhaseLoading(false);
    setLoading(true);
    getCvQuestions('cv')
      .then((list) => (list || []).map(normalizeCvQuestion).filter((q) => q.key)
        .sort((a, b) => a.sortOrder - b.sortOrder))
      .then((cvQuestions) => {
        setQuestions(cvQuestions);
        const first = cvQuestions[0];
        setMessages([
          { role: 'ai', text: INTRO },
          ...(first?.section ? [{ role: 'section', text: first.section }] : []),
          ...(first ? [{ role: 'ai', text: first.text }] : []),
        ]);
        if (!first) startAbroadPhase({}, [{ role: 'ai', text: INTRO }]);
      })
      .catch(() => startAbroadPhase({}, [{ role: 'ai', text: INTRO }]))
      .finally(() => setLoading(false));
  };

  const current = finished ? null : questions[currentIdx];
  // Koşulu sağlanmayan sorular sayıma girmez
  const visibleQuestions = questions.filter((q) => isAbroadQuestionVisible(q, answers));
  const answeredCount = visibleQuestions.filter((q) => answers[q.key]).length;
  const progress = visibleQuestions.length
    ? Math.round((answeredCount / visibleQuestions.length) * 100)
    : 0;

  return (
    <div className="abroad-page">
      <SEO title="Yurt Dışı Planım" path="/panel/yurtdisi" noindex />
      <div className="abroad__container">
        <BackButton />

        <div className="abroad__header">
          <div className="abroad__header-icon">
            <Plane size={24} />
          </div>
          <h1 className="abroad__title">Yurt Dışı Planım</h1>
          <p className="abroad__subtitle">
            ABD ve Kanada'da çalışma hedefini, vize durumunu ve uyumunu netleştirelim.
          </p>
        </div>

        <p className="abroad__disclaimer">
          <AlertCircle size={13} />
          Bu bölüm yalnızca resmi göçmenlik kaynaklarından derlenen genel bilgilendirme
          içeriğidir. Resmi vize/oturum başvurusu yerine geçmez ve vize garantisi sunmaz.
        </p>

        {loading ? (
          <div className="abroad__loading">
            <Loader2 size={22} className="abroad__spinner" />
            <p>Cevapların yükleniyor...</p>
          </div>
        ) : (
          <>
            <div className="abroad__progress">
              <div className="abroad__progress-bar">
                <div className="abroad__progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <span className="abroad__progress-text">
                {phase === 'cv' ? 'CV Bilgileri' : 'Yurt Dışı Planı'} · {' '}
                {current?.section ? `${current.section} · ` : ''}{answeredCount}/{visibleQuestions.length} soru
              </span>
            </div>

            <div className="abroad__chat">
              <div className="abroad__messages">
                {messages.map((m, i) => (
                  m.role === 'section' ? (
                    <div key={i} className="abroad__section">
                      <span>{m.text}</span>
                    </div>
                  ) : (
                    <div key={i} className={`abroad__msg abroad__msg--${m.role}`}>
                      <div className="abroad__msg-avatar">
                        {m.role === 'ai' ? <Bot size={15} /> : <UserIcon size={15} />}
                      </div>
                      <div className="abroad__msg-bubble">{m.text}</div>
                    </div>
                  )
                ))}

                {typing && (
                  <div className="abroad__msg abroad__msg--ai">
                    <div className="abroad__msg-avatar"><Bot size={15} /></div>
                    <div className="abroad__msg-bubble abroad__msg-typing">
                      <span /><span /><span />
                    </div>
                  </div>
                )}
                {phaseLoading && (
                  <p className="abroad__phase-loading">
                    <Loader2 size={13} className="abroad__spinner" /> Yurt dışı soruları hazırlanıyor...
                  </p>
                )}
                <div ref={endRef} />
              </div>

              {/* Cevap alanı */}
           {!finished && (current || followUp) && (
                <div className="abroad__answer">
                  {(saving || saveError) && (
                    <p className={`abroad__inline-status ${saveError ? 'abroad__inline-status--error' : ''}`}>
                      {saving ? (
                        <><Loader2 size={12} className="abroad__spinner" /> Kaydediliyor...</>
                      ) : (
                        <><AlertCircle size={12} /> {saveError}</>
                      )}
                    </p>
                  )}
                  {analyzing && (
                    <p className="abroad__inline-status">
                      <Loader2 size={12} className="abroad__spinner" /> Cevabın değerlendiriliyor...
                    </p>
                  )}

                  {followUp ? (
                    <div className="abroad__input-row">
                      <textarea
                        className="abroad__input abroad__input--multiline"
                        rows={2}
                        value={input}
                        placeholder={current?.placeholder || 'Biraz daha detay verir misin?'}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            answerCurrent(input);
                          }
                        }}
                        disabled={typing}
                        autoFocus
                      />
                      {speechSupported && (
                        <button
                          type="button"
                          className={`abroad__mic ${isListening ? 'abroad__mic--active' : ''}`}
                          onClick={toggleListening}
                          disabled={typing || analyzing}
                          aria-label={isListening ? 'Dinlemeyi durdur' : 'Sesle yaz'}
                          title={isListening ? 'Dinlemeyi durdur' : 'Sesle yaz'}
                        >
                          {isListening ? <MicOff size={17} /> : <Mic size={17} />}
                        </button>
                      )}
                      <button
                        className="abroad__send"
                        onClick={() => answerCurrent(input)}
                        disabled={typing || !input.trim()}
                        aria-label="Gönder"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  ) : current.type === 'checkbox' ? (
                    <>
                      <div className="abroad__choices">
                        {(current.choices || []).map((c) => (
                          <button
                            key={c.value}
                            className={`abroad__choice ${checked.includes(c.value) ? 'abroad__choice--on' : ''}`}
                            onClick={() => setChecked((prev) =>
                              prev.includes(c.value) ? prev.filter((v) => v !== c.value) : [...prev, c.value]
                            )}
                            disabled={typing}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                      <button
                        className="abroad__continue"
                        onClick={() => answerCurrent(checked.join(', '))}
                        disabled={typing || checked.length === 0}
                      >
                        Devam
                      </button>
                    </>
                  ) : current.type === 'choice' ? (
                    <div className="abroad__choices">
                      {(current.choices || []).map((c) => (
                        <button
                          key={c.value}
                          className="abroad__choice"
                          onClick={() => answerCurrent(c.value)}
                          disabled={typing}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="abroad__input-row">
                      {current.type === 'textarea' ? (
                        <textarea
                          className="abroad__input abroad__input--multiline"
                          rows={2}
                          value={input}
                          placeholder={current.placeholder || 'Cevabını yaz...'}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              answerCurrent(input);
                            }
                          }}
                          disabled={typing}
                          autoFocus
                        />
                      ) : (
                        <input
                          className="abroad__input"
                          type={current.type === 'month' && acceptsOngoingAnswer(current)
                            ? 'text'
                            : (['email', 'tel', 'month'].includes(current.type) ? current.type : 'text')}
                          inputMode={current.type === 'tel' ? 'tel' : current.type === 'email' ? 'email' : undefined}
                          value={input}
                          placeholder={current.placeholder
                            || (acceptsOngoingAnswer(current) ? 'Örn: 2024 veya Devam' : 'Cevabını yaz...')}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && answerCurrent(input)}
                          disabled={typing}
                          autoFocus
                        />
                      )}
                      {speechSupported && (
                        <button
                          type="button"
                          className={`abroad__mic ${isListening ? 'abroad__mic--active' : ''}`}
                          onClick={toggleListening}
                          disabled={typing || analyzing}
                          aria-label={isListening ? 'Dinlemeyi durdur' : 'Sesle yaz'}
                          title={isListening ? 'Dinlemeyi durdur' : 'Sesle yaz'}
                        >
                          {isListening ? <MicOff size={17} /> : <Mic size={17} />}
                        </button>
                      )}
                      <button
                        className="abroad__send"
                        onClick={() => answerCurrent(input)}
                        disabled={typing || !input.trim()}
                        aria-label="Gönder"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {finished && (
                <div className="abroad__done">
                  {saving && (
                    <p className="abroad__status">
                      <Loader2 size={15} className="abroad__spinner" /> Kaydediliyor...
                    </p>
                  )}
                  {saved && !saving && (
                    <p className="abroad__status abroad__status--ok">
                      <CheckCircle2 size={15} /> Cevapların kaydedildi
                    </p>
                  )}
                  {saveError && (
                    <p className="abroad__status abroad__status--error">
                      <AlertCircle size={15} /> {saveError}
                    </p>
                  )}
                  <button className="abroad__restart" onClick={restart}>
                    <RotateCcw size={15} /> Baştan güncelle
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AbroadPage;
